import React from 'react'
import { Box, List, ListItem, styled, Typography } from '@mui/material'

import Image from '../Image'

import { LinkAnimation } from './LinkAnimation'

type KeyFeaturesProps = {
  data: {
    title: string
    icon_with_text_items: {
      title: string
      icon: {
        asset: {
          url: string
        }
      }
    }[]
    addlink: {
      cmnexternallink: boolean
      cmnlink: string
      cmnlinktext: string
    }
    show_divider: boolean
  }
}

const TopGradient = styled(Box)`
  width: 164px;
  height: 6px;
  background: url('/images/key-features-gradient-bar.svg') no-repeat;
  background-size: cover;
  content: '';
  position: absolute;
  right: 12px;
  top: -10px;
`

const BackgroundEffect = styled(Box)`
  ${({ theme }) => `
    width: 75%;
    background: linear-gradient(to right, #212735, #0f1423);
    border-radius: ${theme.spacing(1.5)};
    bottom: 0;
    right: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: -1;
  `}
`

const BottomLayer = styled(Box)`
  ${({ theme }) => `
    width: 25%;
    height: 25px;
    background: #212735;
    border: 1px solid ${theme.palette.border.main};
    border-top: none;
    border-bottom-left-radius: ${theme.spacing(1.5)};
    border-right: 0;
    bottom: -24px;
    left: -1px;
    position: absolute;

    :after {
      position: absolute;
      content: '';
      width: 24px;
      height: 24px;
      background: #212735;
      bottom: -1px;
      transform: skew(-30deg);
      left: auto;
      right: -15px;
      border: 1px solid ${theme.palette.border.main};
      border-bottom-right-radius: ${theme.spacing(1)};
      border-left: none;
      border-top: none;
    }
  `}
`

export const KeyFeatures = ({ data }: KeyFeaturesProps) => (
  <Box
    sx={{
      pt: { xs: 8, mobile: 11, laptop: 12 }
    }}>
    <Box
      sx={{
        maxWidth: 1152,
        mx: 'auto',
        pb: { xs: 8, mobile: 10, laptop: 12 },
        px: {
          xs: 3,
          mobile: 8,
          laptop: 4,
          desktop: 0
        }
      }}>
      <Typography
        variant='h1'
        component='h2'
        sx={{
          fontSize: { laptop: 54 },
          letterSpacing: '-0.04em',
          textAlign: 'center'
        }}>
        {data.title}
      </Typography>

      <List
        disablePadding
        sx={{
          alignItems: 'flex-start',
          display: 'grid',
          rowGap: { xs: 8, laptop: 9 },
          columnGap: 7,
          gridTemplateColumns: { laptop: 'repeat(3, 1fr)' },
          mt: { xs: 5, laptop: 12 }
        }}>
        {data.icon_with_text_items.map(item => (
          <ListItem key={item.title} disablePadding>
            <Box
              sx={{
                width: '100%',
                alignItems: 'center',
                background: '#212735',
                border: 1,
                borderColor: 'border.main',
                borderRadius: 1.5,
                borderBottomLeftRadius: 0,
                display: { mobile: 'flex', laptop: 'block' },
                minHeight: { xs: 176, mobile: 96, laptop: 180 },
                position: 'relative',
                pb: 2.5,
                pt: 3.5,
                px: 3,
                zIndex: 1
              }}>
              <Box
                sx={{
                  img: {
                    width: { mobile: 100, laptop: 'auto' },
                    maxHeight: 50
                  }
                }}>
                <Image url={item.icon.asset?.url} />
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: 18, mobile: 20, laptop: 22 },
                  letterSpacing: '-0.005em',
                  lineHeight: 1.4,
                  maxWidth: { xs: 364, laptop: 216 },
                  ml: { mobile: 3, laptop: 0 },
                  mt: { xs: 2.5, mobile: 0, laptop: 2.5 }
                }}>
                {item.title}
              </Typography>
              <TopGradient />
              <BackgroundEffect />
              <BottomLayer />
            </Box>
          </ListItem>
        ))}
      </List>

      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: { xs: 'center', mobile: 'flex-end' },
          mt: { xs: 9, mobile: 8, laptop: 6 }
        }}>
        <LinkAnimation
          link={data.addlink.cmnlink}
          text={data.addlink.cmnlinktext}
          externalLink={data.addlink.cmnexternallink}
          sx={{
            fontSize: { xs: 18, laptop: 24 },
            mr: { xs: 2, mobile: 4, laptop: 2.5 }
          }}
        />
      </Box>
    </Box>

    {data.show_divider && <Box className='section-divider' />}
  </Box>
)
